import { type BoardColumn } from '@gorilla/common/src/lib/monday-api/api';
import { type PortalSettings, type FieldValues } from '@gorilla/portal-shared/src/schemas';
import { ColumnSettingsBoardSourceFieldDescriptor, SectionDescriptor, type SubitemsFieldValue } from '@gorilla/portal-shared/src/types';
import { Box, Flex, Icon, IconButton, Toggle, Divider, Text, Tooltip, Label } from '@vibe/core';
import { Subitems as SubitemsIcon, Show as ShowIcon, LearnMore, Team } from '@vibe/icons';
import { ColumnSettings } from '../ColumnSettings';
import { ColumnPicker } from '../../shared/components/ColumnPicker';
import { useBoardSourceResolver } from '../../hooks/use-board-source-resolver';
import { ExternalLink } from '../ExternalLink';

type SubitemsFieldProps = {
  value: SubitemsFieldValue;
  fieldDescriptor: ColumnSettingsBoardSourceFieldDescriptor;
  sectionDescriptor: SectionDescriptor;
  portalSettings: PortalSettings;
  sectionFieldValues: FieldValues;
  onChange: (value: SubitemsFieldValue) => void;
  clientBoardSubitemsBoardId: number | null;
  screenMode: 'small' | 'medium' | 'large';
};

export function SubitemsField({
  value,
  fieldDescriptor,
  portalSettings,
  onChange,
  sectionDescriptor,
  sectionFieldValues,
  clientBoardSubitemsBoardId,
  screenMode,
}: SubitemsFieldProps) {
  const { subboard, supportsSubitems, board, isLoading, boardId } = useBoardSourceResolver({
    portalSettings,
    sectionDescriptor,
    clientBoardSubitemsBoardId,
    sectionFieldValues,
  });

  if (!boardId || isLoading || !board || !supportsSubitems || !subboard) {
    return null;
  }

  const showSubitems = !!value?.showSubitems;

  return (
    <Box
      className="section__field section__field--subitems"
      rounded="small"
      border={true}
      borderColor="layoutBorderColor"
      backgroundColor="greyBackgroundColor"
    >
      <Box padding="medium">
        <Flex gap={8}>
          <Icon icon={SubitemsIcon} size={24} style={{ transform: 'translate3d(0,-2px,0)' }} />
          <Text type="text1" weight="bold" align="start" ellipsis={true} style={{ marginRight: '8px' }}>
            Subitems of {board.name}
          </Text>
          {showSubitems && (
            <Tooltip
              content="Only up to 100 subitems can be fetched and displayed in your portal due to monday API limits."
              position="right"
              showOnDialogEnter={false}
            >
              <Label text="Limited to 100" color="dark" kind="line" />
            </Tooltip>
          )}
          <Box style={{ marginLeft: 'auto' }}>
            <Flex
              justify="start"
              gap={10}
              style={{
                flex: 1,
              }}
            >
              <Flex
                style={{
                  marginRight: 'auto',
                }}
              >
                <Box
                  style={{
                    pointerEvents: 'none',
                    opacity: 0,
                  }}
                >
                  <IconButton icon={ShowIcon} kind="secondary" color="primary" size="small" onClick={() => {}} />
                </Box>
                <Box
                  style={{
                    marginRight: '-6px',
                    marginLeft: '0px',
                  }}
                >
                  <Toggle
                    offOverrideText=""
                    onOverrideText=""
                    size="small"
                    isSelected={showSubitems}
                    onChange={(isSelected) => {
                      onChange({ ...(value || {}), showSubitems: isSelected });
                    }}
                  />
                </Box>
              </Flex>
            </Flex>
          </Box>
        </Flex>
      </Box>
      {showSubitems ? (
        <>
          <Divider withoutMargin />
          <Box>
            <ColumnSettings
              board={subboard}
              showEditItemValueButton={true}
              columnSettings={value?.columnSettings || {}}
              onChange={(newColumnSettings) => {
                onChange({ ...(value || {}), columnSettings: newColumnSettings });
              }}
              //screenMode="medium" // Always true for Subitems
              screenMode={screenMode}
              isDisplayOrderAutoWidth={true}
              mode="table"
            />
          </Box>
          <Divider withoutMargin />

          <Box padding="medium">
            <Flex
              gap={24}
              style={{
                height: '32px',
              }}
            >
              <Flex gap={8}>
                <Icon icon={Team} size={20} style={{ transform: 'translate3d(0,0px,0)' }} />
                <Text type="text2" weight="bold" align="start">
                  Client filter
                </Text>
                <div>
                  <ExternalLink
                    href="https://getgorilla.app/products/client-portal/docs/user-guides/subitems#anchor-7806"
                    style={{ color: '#323338' }}
                  >
                    <Tooltip
                      title="What's this?"
                      content="Show all subitems, or only those assigned to the current client via a Connect boards column on the subitem. Click to learn more."
                      moveBy={{ main: 5, secondary: -15 }}
                      position="right"
                      showOnDialogEnter={false}
                    >
                      <Icon icon={LearnMore} size={16} style={{ transform: 'translate3d(0,2px,0)' }} />
                    </Tooltip>
                  </ExternalLink>
                </div>
              </Flex>
              <Box style={{ marginLeft: 'auto', width: '180px' }}>
                <ColumnPicker
                  placeholder="Show all subitems"
                  columns={(() => {
                    const connectBoardsColumns = subboard.columns.filter((col) => col.type === 'board_relation');
                    const clientColumnId = value?.clientColumnId;
                    const isClientColumnDeleted = clientColumnId && !connectBoardsColumns.some((col) => col.id === clientColumnId);
                    if (isClientColumnDeleted) {
                      connectBoardsColumns.unshift({
                        id: clientColumnId,
                        title: `Deleted column (${clientColumnId})`,
                        type: 'board_relation',
                      } as BoardColumn);
                    }
                    return connectBoardsColumns;
                  })()}
                  value={value?.clientColumnId}
                  clearable={true}
                  isSmall={true}
                  hideIcons={true}
                  onChange={(column) => {
                    onChange({ ...(value || {}), clientColumnId: column?.id });
                  }}
                />
              </Box>
            </Flex>
          </Box>
        </>
      ) : null}
    </Box>
  );
}
